import type { DateTime } from "luxon";
import { getStaffingDate } from "./dateUtils";
import { AbsenceType, type CalledOffShift, ChangesSource } from "./models";

/**
 * Canonical table of the `ChangesSource` values that get a friendly label instead of showing the
 * raw enum value. Shared so `useCallOffSourceLabel` (translated, for the live UI) and the daily
 * printout's Call Outs export (hardcoded English — the export pipeline doesn't run i18n; see
 * `downloads/dailyPrintout/render/callOutsSection.ts`) can't drift on WHICH sources are specially
 * labeled, even though each renders different text for them (translated vs. plain English).
 */
export const CALL_OFF_SOURCE_LABEL_ENTRIES: ReadonlyArray<{ source: ChangesSource; translationKey: string; english: string }> = [
	{ source: ChangesSource.SupportLineSms, translationKey: "call.in.source.support.line.sms", english: "Support Line (SMS)" },
	{ source: ChangesSource.SupportLinePhone, translationKey: "call.in.source.support.line.phone", english: "Support Line (Phone)" },
	{ source: ChangesSource.SupportLine, translationKey: "call.in.source.support.line", english: "Support Line" },
	{ source: ChangesSource.NurseApp, translationKey: "call.in.source.app", english: "App" },
];

/** Canonical "yyyy-MM-dd" key used by every call-in-by-day lookup, so callers can't drift on format. */
export const STAFFING_DATE_KEY_FORMAT = "yyyy-MM-dd";

/**
 * Root of every `useGetLimitedCallOffs` query key, and the prefix `useInvalidateCallIns` invalidates.
 * Shared so those two are the same symbol rather than two string literals that agree by convention.
 */
export const CALL_INS_QUERY_KEY_ROOT = "limitedCallOff";

/** The sentinel `message` a cancelled call-in is stamped with — filtered out of every call-off read
 * (`useGetLimitedCallOffs`) so a discarded row never reappears as a live one. */
export const callInCancelMessage = "Deleted by mistake";

/**
 * The removal reasons that actually mean "the nurse told us she isn't coming".
 *
 * `GET /team/call-offs/limit` returns **every** removal that isn't a system message, which is right for
 * the removals audit grid (`LastCallIns`, `/call-ins`) but far too wide for anything that draws a phone
 * icon: it also carries the facility cutting the shift (`Overstaffed`, `Low Census`, `Cancel`),
 * discipline (`Suspension`), the unknowable (`Other`), and years of free text (`switch`, `off`, `r/o`,
 * `error`, `mistake`). None of those are a nurse calling in, and `department_call_in_reason` does not
 * help tell them apart — despite its name it is the removal-reason dropdown, and its defaults include
 * every one of the facility-side reasons above.
 *
 * Deliberately narrower than the audit grid and WIDER than the disciplinary report's
 * `basic_call_off_messages` (`backend/database/reports.py`), which only counts `Call-In` + no-show
 * because those are the only two that carry attendance points. Sick / Child Care / Family Obligations /
 * Appointment / Bereavement earn no points but are still the nurse calling out, which is what these
 * surfaces show. `Child Care` only ever arrives through a self-serve channel (bedside app, phone IVR,
 * Annie) — same as `Sick` and `Appointment` — never through the manager's removal-reason dropdown.
 *
 * `No Show No Call` is excluded on purpose: it is an absence, but the defining fact is that she did
 * *not* call, so labelling it "Call in" under a phone glyph would state the opposite of what happened.
 * The disciplinary report keeps it as its own violation type for the same reason.
 *
 * Matched case-insensitively, since these land in `nurse_in_shift.message` as free text.
 */
const CALL_IN_REASONS: ReadonlySet<string> = new Set(
	[AbsenceType.CALL_IN, AbsenceType.SICK, AbsenceType.CHILD_CARE, AbsenceType.FAMILY_OBLIGATIONS, AbsenceType.APPOINTMENT, AbsenceType.BEREAVEMENT].map(
		(reason) => reason.toLowerCase(),
	),
);

/** Whether a removal reason means the nurse called out. Unset/free-text reasons are not call-ins. */
export const isCallInReason = (message: string | undefined): boolean => !!message && CALL_IN_REASONS.has(message.trim().toLowerCase());

/**
 * Whether a reason label is the catch-all "Other" that opens a free-text box.
 *
 * Matched on the label's text rather than on `AbsenceType.OTHER`, because the pickers are now driven by
 * `department_call_in_reason`: those labels are admin-typed free text, and the uniqueness index is on
 * `lower(label)`, so a department can legitimately carry its own "other" / "Other ".
 */
export const isOtherReasonLabel = (label: string | undefined | null): boolean => label?.trim().toLowerCase() === AbsenceType.OTHER.toLowerCase();

/**
 * A reason a nurse may pick when calling off her own shift: the ones that mean she called out, plus
 * "Other" for anything the department didn't anticipate.
 *
 * Deliberately narrower than the department's full list, which also carries the facility cutting the
 * shift (Overstaffed, Low Census, Cancel), discipline (Suspension) and No Show No Call. A nurse
 * self-reporting "Low Census" would also vanish from every call-in surface, since `isCallInReason`
 * excludes exactly those.
 */
export const isNurseSelectableReason = (reason: { label: string }): boolean => isCallInReason(reason.label) || isOtherReasonLabel(reason.label);

/**
 * The `absence.type.*` translation key for a reason label, when one exists.
 *
 * Only these five labels were ever translated (the keys pre-date department-configured reasons), and the
 * translations are NOT the label verbatim — `absence.type.family.obligations` is "Family Emergency", and
 * `absence.type.call-in` is "Call In" without the hyphen. So nurse-facing surfaces translate a label they
 * recognise and render the rest verbatim; a department with custom reasons gets a mixed-language list,
 * which beats regressing the five labels every department actually has to English.
 */
const ABSENCE_TYPE_KEY_BY_LABEL: ReadonlyMap<string, string> = new Map(
	[AbsenceType.CALL_IN, AbsenceType.SICK, AbsenceType.CHILD_CARE, AbsenceType.FAMILY_OBLIGATIONS, AbsenceType.OTHER].map((type) => [
		type.toLowerCase(),
		`absence.type.${type.toLowerCase().replace(/\s+/g, ".")}`,
	]),
);

export const absenceTypeTranslationKey = (label: string): string | undefined => ABSENCE_TYPE_KEY_BY_LABEL.get(label.trim().toLowerCase());

/**
 * Resolve a stored `nurse_in_shift.message` to one of the department's configured reason labels.
 *
 * Exact (case-insensitive) match first, then the LONGEST configured label contained in the message.
 * Longest — not last — matters now that the candidate list is the department's own, drag-reorderable
 * list: a "keep the last match" loop lets a reorder silently change what an old row resolves to.
 * No match (a renamed/archived reason, years of free text) returns `undefined`; the caller is expected
 * to keep showing the stored value rather than relabelling it.
 *
 * The containment test is WORD-BOUNDED. A raw substring match relabels free text that merely spells a
 * label inside a longer word — "Mother in hospital" and "switch with another employee" both contain
 * "other" — so those rows would show as the catch-all instead of being preserved verbatim. Boundaries
 * are non-alphanumeric rather than `\b`, so a label with punctuation ("Call-In") still matches.
 */
const isWordBounded = (haystack: string, needle: string, at: number): boolean => {
	const before = haystack[at - 1];
	const after = haystack[at + needle.length];
	return (before === undefined || !/[a-z0-9]/.test(before)) && (after === undefined || !/[a-z0-9]/.test(after));
};

export const resolveCallInReasonLabel = (message: string | undefined | null, labels: readonly string[]): string | undefined => {
	const normalizedMessage = message?.trim().toLowerCase();
	if (!normalizedMessage) return undefined;
	let longestMatch: { label: string; length: number } | undefined;
	for (const label of labels) {
		const candidate = label.trim().toLowerCase();
		if (!candidate) continue;
		if (candidate === normalizedMessage) return label;
		// Every occurrence, not just the first: "another Sick day" must still match "Sick".
		let at = normalizedMessage.indexOf(candidate);
		while (at !== -1 && !isWordBounded(normalizedMessage, candidate, at)) at = normalizedMessage.indexOf(candidate, at + 1);
		if (at === -1) continue;
		if (!longestMatch || candidate.length > longestMatch.length) longestMatch = { label, length: candidate.length };
	}
	return longestMatch?.label;
};

/**
 * Narrow a raw `/team/call-offs/limit` payload to the call-ins. Every call-in surface funnels through
 * this, so the reason set is decided in exactly one place.
 */
export const filterCallIns = (rows: CalledOffShift[]): CalledOffShift[] => rows.filter((row) => isCallInReason(row.message));

/**
 * When the nurse actually called in.
 *
 * Three columns can answer this:
 * - `callInTime` — the manager typed it in the removal dialog. The only one that can deliberately
 *   precede the removal, though in practice it rarely does: the dialog defaults it to "now" and
 *   managers keep the default, so real rows sit within a minute of `deletedAt`.
 * - `deletedAt` — when the shift was removed in IHH. For the self-serve channels (app, SMS, phone
 *   IVR) nobody is asked for a call time, so the removal *is* the call.
 * - `updatedAt` — last resort for legacy rows written before `deleted_at` existed, and for demo data.
 *
 * Same precedence the disciplinary report uses server-side, so the two surfaces never disagree about
 * how late a call-in was. Expect it to change the answer for very few rows — it is here to be correct
 * when a manager does back-date, not because the columns usually differ.
 *
 * DEFAULT: the `??` chain is a domain precedence, not a guard against absent data — each column is the
 * correct answer for a different recording channel, and at least one is always set (`updated_at` is
 * `NOT NULL` server-side). Collapsing it to "require `callInTime`" would blank the time on every
 * app/SMS/phone call-in, which is the large majority.
 */
export const resolveCallInTime = (callIn: CalledOffShift): DateTime => callIn.callInTime ?? callIn.deletedAt ?? callIn.updatedAt;

/**
 * The staffing day a call-in belongs to — the day its *shift* would have been worked, not the day the
 * call came in. A night shift starting 19:00 belongs to that calendar day; one starting 00:30 under an
 * 04:00 `dayStart` belongs to the previous one, which is why this cannot be a plain `toFormat`.
 */
export const callInStaffingDateKey = (callIn: CalledOffShift, dayStart: string, graceMinutes: number): string =>
	getStaffingDate(callIn.startTime, dayStart, graceMinutes).toFormat(STAFFING_DATE_KEY_FORMAT);

/**
 * Group call-ins by the staffing day of the shift they removed, so a calendar cell can look up its own
 * day in O(1). A day can hold several (a double, or a nurse who called off twice), and the list stays
 * ordered earliest-shift-first so a multi-call-in day reads top-to-bottom by time.
 */
export const buildCallInsByStaffingDate = (callIns: CalledOffShift[], dayStart: string, graceMinutes: number): Map<string, CalledOffShift[]> => {
	const byDate = new Map<string, CalledOffShift[]>();
	for (const callIn of callIns) {
		const key = callInStaffingDateKey(callIn, dayStart, graceMinutes);
		const existing = byDate.get(key);
		if (existing) {
			existing.push(callIn);
		} else {
			byDate.set(key, [callIn]);
		}
	}
	for (const dayCallIns of byDate.values()) {
		dayCallIns.sort((a, b) => a.startTime.toMillis() - b.startTime.toMillis());
	}
	return byDate;
};
